/**
 * Editor parametrů dopravního režimu — včetně tvorby vlastních režimů.
 * Ikona se vybírá z ikonového systému; emoji je povolený fallback
 * pro vlastní režimy (zapíše se do textového pole).
 */
import { defaultEffort } from '../core/zones';
import { modeDataSourceNote } from '../core/modes';
import type { ModeProfile } from '../core/types';
import type { Store } from '../state/store';
import { openDialog } from './dialogs';
import { el, clear } from './dom';
import { icon, isIconName, type IconName } from './icons';

/** Ikony nabízené pro dopravní režimy. */
const MODE_ICONS: IconName[] = [
  'walk', 'run', 'bike', 'ebike', 'scooter', 'skate', 'car', 'taxi', 'bus', 'tram', 'train', 'ferry', 'combined', 'custom', 'star', 'gauge',
];

export function openModeEditor(store: Store, modeId: string, onChanged: () => void): void {
  const mode = store.get().modes.find((m) => m.id === modeId);
  if (!mode) return;
  const isCombined = mode.kind === 'combined';
  const isPt = mode.kind === 'pt';

  const f = {
    name: text(mode.name),
    color: el('input', { type: 'color', value: toHex(mode.color), className: 'input', style: 'height:36px;padding:3px' }) as HTMLInputElement,
    speedKmh: num(mode.speedKmh, 0.5, 300, 0.5),
    circuity: num(mode.circuity, 1, 3, 0.05),
    setupMin: num(mode.setupMin, 0, 120, 0.5),
    teardownMin: num(mode.teardownMin, 0, 120, 0.5),
    waitMin: num(mode.waitMin, 0, 180, 0.5),
    costFixed: num(mode.costFixed, 0, 10000, 1),
    costPerKm: num(mode.costPerKm, 0, 1000, 0.1),
    emissionsPerKm: num(mode.emissionsPerKm, 0, 1000, 1),
    comfort: num(mode.comfort, 1, 5, 0.5),
    effort: num(mode.effort ?? defaultEffort(mode.kind), 1, 5, 0.5),
    reliability: num(Math.round(mode.reliability * 100), 10, 100, 5),
    maxDistanceKm: num(mode.maxDistanceKm ?? 0, 0, 1000, 1),
    accessible: check(mode.accessible),
    carFree: check(mode.carFree),
  };

  // Výběr ikony: mřížka systémových ikon + textové pole pro vlastní emoji.
  let chosenIcon = mode.icon;
  const emojiInput = el('input', {
    className: 'input input--small',
    type: 'text',
    value: isIconName(mode.icon) ? '' : mode.icon,
    placeholder: 'vlastní emoji…',
    'aria-label': 'Vlastní emoji místo ikony',
    style: 'width:110px',
  }) as HTMLInputElement;
  const iconGrid = el('div', { className: 'icon-grid', role: 'radiogroup', 'aria-label': 'Ikona režimu' });
  const renderIconGrid = (): void => {
    clear(iconGrid);
    for (const name of MODE_ICONS) {
      iconGrid.append(
        el(
          'button',
          {
            className: `icon-pick${chosenIcon === name ? ' icon-pick--on' : ''}`,
            type: 'button',
            role: 'radio',
            'aria-checked': String(chosenIcon === name),
            'aria-label': `Ikona ${name}`,
            onclick: () => {
              chosenIcon = name;
              emojiInput.value = '';
              renderIconGrid();
            },
          },
          icon(name, { size: 18 }),
        ),
      );
    }
  };
  renderIconGrid();
  emojiInput.addEventListener('input', () => {
    if (emojiInput.value.trim()) {
      chosenIcon = emojiInput.value.trim().slice(0, 4);
      renderIconGrid();
    }
  });

  const field = (label: string, input: HTMLElement, hint?: string): HTMLElement =>
    el('div', {}, el('label', { className: 'label' }, label), input, hint ? el('div', { className: 'hint' }, hint) : null);

  const body = el(
    'div',
    {},
    el(
      'div',
      { className: 'field-grid' },
      field('Název', f.name),
      field('Barva na mapě', f.color),
      !isPt && !isCombined ? field('Rychlost (km/h)', f.speedKmh, 'Průměrná cestovní rychlost.') : null,
      !isPt && !isCombined
        ? field('Nepřímost trasy (×)', f.circuity, 'Kolikrát je reálná trasa delší než vzdušná čára. 1,0 = přímo.')
        : null,
      field('Zdržení na startu (min)', f.setupMin, 'Příprava: odemknutí, nastartování…'),
      field('Zdržení v cíli (min)', f.teardownMin, 'Parkování, zamknutí, přezutí…'),
      field('Čekání (min)', f.waitMin, 'Např. čekání na sdílené vozidlo.'),
      field('Fixní cena (Kč)', f.costFixed),
      field('Cena za km (Kč)', f.costPerKm),
      field('Emise (g CO₂/km)', f.emissionsPerKm),
      field('Komfort (1–5)', f.comfort),
      field('Fyzická náročnost (1–5)', f.effort, 'Pro metriku zón „Fyzická náročnost". 1 = bez námahy.'),
      field('Spolehlivost (%)', f.reliability, 'Nižší spolehlivost = širší interval nejistoty.'),
      !isPt && !isCombined ? field('Max. vzdálenost (km)', f.maxDistanceKm, '0 = bez omezení. Nad limit se režim nenabízí — omezuje i dosah barev v zónách.') : null,
    ),
    el('div', { className: 'label', style: 'margin-top:8px' }, 'Ikona'),
    el('div', { className: 'row row--wrap' }, iconGrid, emojiInput),
    el(
      'div',
      { className: 'row row--wrap', style: 'margin-top:4px' },
      el('label', { className: 'switch' }, f.accessible, el('span', {}, 'Bezbariérový')),
      el('label', { className: 'switch' }, f.carFree, el('span', {}, 'Funguje bez vlastního auta')),
    ),
    isPt
      ? el('div', { className: 'alert alert--info' }, 'Časy MHD se počítají z jízdních řádů (GTFS import / ruční linky / označené demo). Tady nastavíte jen vzhled a obecné vlastnosti.')
      : null,
    el(
      'div',
      { className: 'hint', style: 'margin-top:8px' },
      el('strong', {}, 'Zdroj dat: '),
      modeDataSourceNote(mode),
    ),
  );

  const footer: HTMLElement[] = [];
  if (mode.userDefined) {
    footer.push(
      el(
        'button',
        {
          className: 'btn',
          style: 'margin-right:auto;color:var(--bad)',
          onclick: () => {
            store.removeMode(mode.id);
            onChanged();
            dlg.close();
          },
        },
        '🗑 Smazat režim',
      ),
    );
  }
  footer.push(
    el('button', { className: 'btn', onclick: () => dlg.close() }, 'Zrušit'),
    el(
      'button',
      {
        className: 'btn btn--primary',
        onclick: () => {
          const patch: Partial<ModeProfile> = {
            name: f.name.value.trim() || mode.name,
            icon: chosenIcon || mode.icon,
            color: f.color.value,
            speedKmh: val(f.speedKmh, mode.speedKmh),
            circuity: val(f.circuity, mode.circuity),
            setupMin: val(f.setupMin, mode.setupMin),
            teardownMin: val(f.teardownMin, mode.teardownMin),
            waitMin: val(f.waitMin, mode.waitMin),
            costFixed: val(f.costFixed, mode.costFixed),
            costPerKm: val(f.costPerKm, mode.costPerKm),
            emissionsPerKm: val(f.emissionsPerKm, mode.emissionsPerKm),
            comfort: Math.min(5, Math.max(1, val(f.comfort, mode.comfort))),
            effort: Math.min(5, Math.max(1, val(f.effort, mode.effort ?? defaultEffort(mode.kind)))),
            reliability: Math.min(1, Math.max(0.1, val(f.reliability, mode.reliability * 100) / 100)),
            accessible: f.accessible.checked,
            carFree: f.carFree.checked,
          };
          const maxD = val(f.maxDistanceKm, mode.maxDistanceKm ?? 0);
          patch.maxDistanceKm = maxD > 0 ? maxD : undefined;
          store.updateMode(mode.id, patch);
          onChanged();
          dlg.close();
        },
      },
      'Uložit',
    ),
  );

  const dlg = openDialog({
    title: `Parametry: ${mode.name}`,
    body,
    footer,
    wide: true,
  });
}

function text(value: string): HTMLInputElement {
  return el('input', { className: 'input', type: 'text', value }) as HTMLInputElement;
}

function num(value: number, min: number, max: number, step: number): HTMLInputElement {
  return el('input', {
    className: 'input',
    type: 'number',
    value: String(Math.round(value * 100) / 100),
    min: String(min),
    max: String(max),
    step: String(step),
  }) as HTMLInputElement;
}

function check(checked: boolean): HTMLInputElement {
  return el('input', { type: 'checkbox', checked }) as HTMLInputElement;
}

function val(input: HTMLInputElement, fallback: number): number {
  const n = Number(input.value.replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
}

/** „#rgb"/„rgb()" → #rrggbb pro input[type=color]; jinak vrátí vstup. */
function toHex(color: string): string {
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
  }
  return '#64748b';
}
